import React from 'react';
import { View } from 'react-native';
import { useRoute } from '@react-navigation/native';

import Text from '../../components/text/Text';
import { Card, EmptyState, ListRow, MachineChip, Screen } from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { getProductChangeQuantities, getStopProductChanges } from '../../utils/getStopProductChanges';
import { useDriverDay } from './DriverDayProvider';
import DriverTrainingBanner from './DriverTrainingBanner';
import { getStopPositionName, getStopSiteName } from './stopDetails';
import useStopDocket from './useStopDocket';

const ProductChangesView = () => {
	const day = useDriverDay();
	const styles = useThemedStyles(createStyles);
	const route = useRoute();
	const { docket, isLoading, error, refetch } = useStopDocket(day, route.params?.docketId);
	const items = getStopProductChanges(docket);
	const stopIndex = day.sortedDockets.findIndex(stop => stop.id === docket?.id);

	return (
		<Screen
			title="Product changes"
			backLabel="Back"
			heading="Product changes"
			subheading={`${items.length} product ${items.length === 1 ? 'change' : 'changes'} at this stop. Removal quantities are estimated until recorded at service.`}
			isLoading={isLoading}
			error={error ? 'This stop could not be loaded. Check your connection and try again.' : undefined}
			onRetry={() => refetch()}
			banner={<DriverTrainingBanner />}
		>
			{docket && items.length > 0 && (
				<Card style={styles.stop}>
					<Text style={styles.caption}>{stopIndex >= 0 ? `Stop ${stopIndex + 1}` : 'Stop'}</Text>
					<Text style={styles.site}>{getStopSiteName(docket)}</Text>
					<View style={styles.position}>
						<Text style={styles.positionName}>{getStopPositionName(docket)}</Text>
						<MachineChip number={docket.machine?.assetId} />
					</View>
					{items.map(item => {
						const { removedQuantity, newQuantity } = getProductChangeQuantities(item);
						return (
							<ListRow
								key={item.id}
								overline={item.selection ? `Selection ${item.selection}` : undefined}
								title={`${item.removedProduct.name || 'Old product'} → ${item.product?.name || 'New product'}`}
								subtitle={`Remove ${removedQuantity} · Add ${newQuantity}`}
							/>
						);
					})}
				</Card>
			)}
			{docket && !items.length && (
				<EmptyState title="No product changes" message="There are no product replacements at this stop." />
			)}
			{!docket && !isLoading && !error && (
				<EmptyState title="Stop not found" message="This stop may have been removed from its run." />
			)}
		</Screen>
	);
};

const createStyles = ({ ui: { colors, space, type } }) => ({
	stop: {
		gap: space.sm,
	},
	caption: {
		...type.caption,
		color: colors.textSecondary,
	},
	site: {
		...type.sectionTitle,
		color: colors.text,
	},
	position: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		alignItems: 'center',
		gap: space.sm,
	},
	positionName: {
		...type.bodySmallStrong,
		color: colors.text,
		flexShrink: 1,
	},
});

export default ProductChangesView;
